/**
 * Global fetch interceptor - installed once at app boot.
 *
 * Every fetch() call in the app benefits from this without any code changes:
 * 1. Automatically injects the localStorage session_token as an Authorization
 *    header for requests to our backend. This is our fallback when httpOnly
 *    session cookies drop out (which happens intermittently in cross-domain /
 *    long-tab / mobile browser scenarios).
 * 2. Injects the CSRF token header on unsafe methods when a csrf_token cookie
 *    is present (SEC-003 defense-in-depth). Requests carrying Authorization:
 *    Bearer already bypass CSRF in the middleware, but this belt-and-suspenders
 *    approach also covers cookie-only clients that never touched authFetch.
 * 3. On 401, clears the stale token so the auth guard can cleanly redirect.
 *
 * Without this, files that use plain `fetch(...)` (instead of `authFetch`) fail
 * silently in production when the cookie is lost, dumping users back to /login
 * mid-flow — even though they still have a valid session_token in localStorage.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function isBackendRequest(input) {
  try {
    const url = typeof input === "string" ? input : input?.url;
    if (!url) return false;
    // Match our backend base URL, or any /api path (relative)
    if (BACKEND_URL && url.startsWith(BACKEND_URL)) return true;
    if (url.startsWith("/api/")) return true;
    // Absolute URL with /api path
    if (/^https?:\/\/[^/]+\/api\//.test(url)) return true;
    return false;
  } catch {
    return false;
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split("; ").find((x) => x.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

export function installFetchInterceptor() {
  if (typeof window === "undefined" || window.__ha_fetch_patched__) return;
  window.__ha_fetch_patched__ = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    // Only augment our backend calls; leave third-party requests untouched.
    if (!isBackendRequest(input)) {
      return originalFetch(input, init);
    }

    const token = localStorage.getItem("session_token");
    const headers = new Headers(init.headers || {});
    const method = (init.method || "GET").toUpperCase();

    // Only add if not already set by the caller (e.g. authFetch)
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // SEC-003: on unsafe methods, add the CSRF header from the readable cookie
    // (if one exists). The backend middleware also skips CSRF when Authorization:
    // Bearer is present, so this is defense-in-depth for cookie-only flows.
    if (UNSAFE_METHODS.has(method) && !headers.has("X-CSRF-Token")) {
      const csrf = readCookie("csrf_token");
      if (csrf) {
        headers.set("X-CSRF-Token", csrf);
      }
    }

    const patchedInit = {
      ...init,
      credentials: init.credentials || "include",
      headers,
    };

    const response = await originalFetch(input, patchedInit);

    // If our token is stale/invalid, drop it so the auth guard can cleanly redirect
    // without ping-ponging back to a protected page. Only clear on 401 from /auth/*
    // endpoints — a 401 on a business endpoint might be a permissions issue, not
    // a bad session.
    if (response.status === 401 && token) {
      const url = typeof input === "string" ? input : input?.url;
      if (url && /\/api\/auth\/(me|session)\b/.test(url)) {
        localStorage.removeItem("session_token");
      }
    }

    return response;
  };
}
